/**
 * Super-Admin → Deployment Pack
 *
 * Generates the per-customer configuration for an on-prem / air-gapped install,
 * so onboarding a regulated customer no longer requires an engineer running
 * shell scripts. Platform-side on purpose: it names a tenant the operator is
 * not a member of.
 *
 * The pack is CONFIGURATION ONLY - kilobytes. The container images ship in the
 * release bundle. The page says so prominently, because an operator who thinks
 * the images are inside will send this to a customer and then field a call
 * about why nothing starts.
 *
 * Every secret in the generated files reads CHANGEME; the customer's own
 * install.sh fills them with `openssl rand`. That is worth understanding before
 * changing anything here: it is why this pack is safe to attach to an email,
 * and why FOCTTA never holds a customer's database password.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Copy,
  Download,
  FileText,
  Loader2,
  Package,
  ShieldCheck,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  generateDeploymentPack,
  listTenants,
  type DeploymentPack,
  type Tenant,
  type TlsMode,
} from '@/lib/tenants-admin-api';

const TLS_OPTIONS: ReadonlyArray<{ value: TlsMode; label: string; hint: string }> = [
  {
    value: 'internal-ca',
    label: 'Internal CA',
    hint: 'Caddy issues its own certificate. No outbound internet. Agent machines must trust the root.',
  },
  {
    value: 'lets-encrypt',
    label: "Let's Encrypt",
    hint: 'Needs outbound 443 and a publicly resolvable name - impossible on a truly air-gapped site.',
  },
  {
    value: 'behind-load-balancer',
    label: 'Behind a load balancer',
    hint: 'Your LB terminates TLS. It must set X-Forwarded-Proto or generated links come out as http.',
  },
];

function downloadText(filename: string, contents: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function DeploymentPack(): JSX.Element {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [version, setVersion] = useState('v1.4.0');
  const [registry, setRegistry] = useState('foctta');
  const [tls, setTls] = useState<TlsMode>('internal-ca');

  const [pack, setPack] = useState<DeploymentPack | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    listTenants({ limit: 200 })
      .then((r) => setTenants(r.tenants))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load tenants'));
  }, []);

  const generate = useCallback(async () => {
    setBusy(true);
    setError(null);
    setPack(null);
    try {
      setPack(await generateDeploymentPack(tenantId, { siteAddress, version, tls, registry }));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setBusy(false);
    }
  }, [tenantId, siteAddress, version, tls, registry]);

  const copy = useCallback((key: string, text: string) => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    });
  }, []);

  const ready = tenantId !== '' && siteAddress.trim() !== '' && version.trim() !== '';
  const field = 'w-full rounded-md border border-border bg-background/40 p-2 text-xs';

  return (
    <PageContainer>
      <PageHeader
        title="Deployment pack"
        subtitle="Per-customer configuration for an on-prem or air-gapped install"
      />

      <GlassCard>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-4 w-4" /> Generate
          </CardTitle>
          <CardDescription>
            Produces the files a customer runs on their own VPS. Configuration only - the
            container images ship in the release bundle.
          </CardDescription>
        </CardHeader>

        <div className="grid gap-3 p-4 pt-0 md:grid-cols-2">
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Tenant</span>
            <select className={field} value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              <option value="">Select a tenant…</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">
              Site address — the hostname THEY will serve on
            </span>
            <input
              type="text"
              className={field}
              value={siteAddress}
              placeholder="foctta.bank.internal"
              onChange={(e) => setSiteAddress(e.target.value.trim().toLowerCase())}
            />
          </label>

          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Bundle version</span>
            <input
              type="text"
              className={field}
              value={version}
              onChange={(e) => setVersion(e.target.value.trim())}
            />
          </label>

          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">
              Registry — leave as <code>foctta</code> if they docker load from the bundle
            </span>
            <input
              type="text"
              className={field}
              value={registry}
              onChange={(e) => setRegistry(e.target.value.trim())}
            />
          </label>

          <label className="text-xs md:col-span-2">
            <span className="mb-1 block text-muted-foreground">TLS</span>
            <select
              className={field}
              value={tls}
              onChange={(e) => setTls(e.target.value as TlsMode)}
            >
              {TLS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-[11px] text-muted-foreground">
              {TLS_OPTIONS.find((o) => o.value === tls)?.hint}
            </span>
          </label>
        </div>

        <div className="flex items-center gap-3 p-4 pt-0">
          <Button onClick={() => void generate()} disabled={!ready || busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Generate pack
          </Button>
          {!ready ? (
            <span className="text-xs text-muted-foreground">
              Pick a tenant and enter the hostname they will serve on.
            </span>
          ) : null}
        </div>
      </GlassCard>

      {error !== null ? (
        <GlassCard className="mt-4 border-destructive/40">
          <div className="flex items-start gap-2 p-4 text-xs">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
            <span>{error}</span>
          </div>
        </GlassCard>
      ) : null}

      {pack !== null ? (
        <>
          <GlassCard className="mt-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" /> No secrets in this pack
              </CardTitle>
              <CardDescription>
                Every credential reads <code>CHANGEME</code>. The customer&apos;s{' '}
                <code>install.sh</code> fills them with <code>openssl rand</code> on their own
                machine, so this pack is safe to send by email and FOCTTA never holds their
                database password.
              </CardDescription>
            </CardHeader>
            <div className="px-4 pb-4">
              <p className="mb-1 text-xs font-medium text-muted-foreground">Not included:</p>
              <ul className="list-inside list-disc text-xs text-muted-foreground">
                {pack.notIncluded.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </div>
          </GlassCard>

          <div className="mt-4 flex items-center gap-2">
            <Badge variant="outline">{pack.tenantName}</Badge>
            <Badge variant="outline">{pack.siteAddress}</Badge>
            <Badge variant="outline">{pack.version}</Badge>
            <Badge variant="outline">{pack.tls}</Badge>
            <Button
              size="sm"
              variant="glass"
              onClick={() => {
                for (const f of pack.files) downloadText(f.filename, f.contents);
                downloadText('MANIFEST.sha256', pack.manifest);
              }}
            >
              <Download className="mr-2 h-4 w-4" /> Download all
            </Button>
          </div>

          {pack.files.map((f) => (
            <GlassCard key={f.filename} className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4" /> {f.filename}
                </CardTitle>
                <CardDescription>{f.purpose}</CardDescription>
              </CardHeader>
              <div className="flex items-center gap-2 px-4">
                <Button size="sm" variant="glass" onClick={() => copy(f.filename, f.contents)}>
                  {copied === f.filename ? (
                    <Check className="mr-2 h-4 w-4" />
                  ) : (
                    <Copy className="mr-2 h-4 w-4" />
                  )}
                  Copy
                </Button>
                <Button
                  size="sm"
                  variant="glass"
                  onClick={() => downloadText(f.filename, f.contents)}
                >
                  <Download className="mr-2 h-4 w-4" /> Download
                </Button>
                <code className="ml-auto text-[10px] text-muted-foreground">
                  sha256 {f.sha256.slice(0, 16)}…
                </code>
              </div>
              <pre className="m-4 max-h-80 overflow-auto rounded-md border border-border bg-background/40 p-3 text-[11px] leading-relaxed">
                {f.contents}
              </pre>
            </GlassCard>
          ))}

          <GlassCard className="mt-4">
            <CardHeader>
              <CardTitle className="text-sm">MANIFEST.sha256</CardTitle>
              <CardDescription>
                Ships with the pack so the customer can run <code>sha256sum -c</code> rather than
                comparing hex by eye.
              </CardDescription>
            </CardHeader>
            <pre className="m-4 overflow-auto rounded-md border border-border bg-background/40 p-3 text-[11px]">
              {pack.manifest}
            </pre>
          </GlassCard>
        </>
      ) : null}
    </PageContainer>
  );
}

export default DeploymentPack;
